import React, { useState } from 'react';
import { Modal, Form, Alert, Spin } from 'antd';
import { connect } from 'react-redux';
import {
	checkHasApprovalHierarchy,
	nullSafeGet,
	nullSafeGetOrElse,
} from '../../utils/DataAccessUtils';
import { ENTITY_REQUEST_ENTITY_TYPES } from '../../types/entity_add_requests';
import { withUserType } from '../withUserType';
import useMountEffect from '../../hooks/useMountEffect';
import OWButton from '../ow_button/OWButton';
import SupplierContactFields from './SupplierContactFields';
import SupplierCoverageFields from './SupplierCoverageFields';
import EntityRequestAnswerFields from '../entity_request_form/EntityRequestAnswerFields';
import ApprovalHierarchyDisplayEntityRequest from '../approval_hierarchy_display_entity_request/ApprovalHierarchyDisplayEntityRequest';
import SimilarSuppliersPanel, {
	getSimilarSuppliersTitle,
} from '../similar_suppliers_panel/SimilarSuppliersPanel';
import useSupplierDuplicateCheck from './useSupplierDuplicateCheck';
import {
	buildSupplierPayload,
	buildAnswers,
	getSupplierRequestInitialValues,
} from './supplierRequestFormUtils';
import {
	entityAddRequestsRestCrudThunks,
	fetchEntityAddRequestApprovalHierarchyPreview,
	submitEntityAddRequest,
} from '../../thunks/entity_add_requests_thunks';
import {
	fetchSuppliersForBuyers,
	supplierPrivateNetworksGetDistinct,
} from '../../thunks/supplier_private_networks_thunks';
import { fetchActiveEntityRequestForm } from '../../thunks/entity_request_forms_thunks';
import {
	problemTypesHeirarchical,
	problemTypesRestCrudThunks,
} from '../../thunks/problem_types_thunks';
import { fetchLocationsWithRegions, locationsRestCrudThunks } from '../../thunks/locations_thunks';

const CONTACT_STEP = 0;
// Only reachable when the duplicate lookup finds similar suppliers — never by simply advancing.
const MATCHES_STEP = 1;
const COVERAGE_STEP = 2;
const QUESTIONS_STEP = 3;
const LAST_STEP = QUESTIONS_STEP;

const CONTACT_STEP_FIELDS = ['companyName', 'contactEmail', 'contactFirstName', 'contactLastName'];

// Redux collection the reused supplier search dispatches into; we only consume the resolved
// matches here, so the exact name is irrelevant beyond keeping it scoped to this form.
const DUPLICATE_SEARCH_COLLECTION = 'requestToAddSupplierDuplicateSearch';

// Whether a record — or the API's preview of one — names any approvers worth linking to. Both carry
// the same `{ hasApprovalHierarchy, approvalHierarchy }` pair, so one check covers both.
const hasApprovalTiers = (candidate) =>
	!!checkHasApprovalHierarchy(candidate) &&
	nullSafeGetOrElse('approvalHierarchy.tierDetails', candidate, []).length > 0;

function RequestToAddSupplierForm({
	visible,
	record,
	hideModal,
	onSuccess,
	problemTypes,
	locations,
	fetchProblemTypes,
	fetchMultipleProblemTypes,
	fetchLocations,
	fetchMultipleLocations,
	fetchActiveForm,
	fetchApprovalHierarchyPreview,
	createRequest,
	updateRequest,
	submitRequest,
	searchSuppliers,
	fetchNetworkMembership,
}) {
	const [form] = Form.useForm();
	const [step, setStep] = useState(CONTACT_STEP);
	const [questions, setQuestions] = useState([]);
	const [questionsLoading, setQuestionsLoading] = useState(true);
	const [savingDraft, setSavingDraft] = useState(false);
	const [submitting, setSubmitting] = useState(false);
	const [errorMessage, setErrorMessage] = useState(undefined);

	// Seeded from an existing record; also set after a successful create so that a submit failure
	// followed by a retry updates the same draft instead of creating a duplicate request.
	const [persistedId, setPersistedId] = useState(nullSafeGet('id', record));
	// A real load failure (network / permission) for the active request form — distinct from the
	// resolved-null "no active form" case. Blocks submission so required questions can't be skipped.
	const [formLoadFailed, setFormLoadFailed] = useState(false);
	// Who would review this request if it were submitted now — see the approvers link below. Only
	// fetched when the record has no tiers of its own.
	const [approvalHierarchyPreview, setApprovalHierarchyPreview] = useState(undefined);

	const recordHasApprovalTiers = hasApprovalTiers(record);

	const showMatchesStep = () => setStep(MATCHES_STEP);
	const showCoverageStep = () => setStep(COVERAGE_STEP);

	const {
		matches: duplicateMatches,
		existingSupplierIds,
		checking: checkingDuplicates,
		checkForDuplicates,
		applyMatchToForm,
		clearSelectedSupplier,
	} = useSupplierDuplicateCheck({
		form,
		searchSuppliers,
		fetchNetworkMembership,
		onMatchesFound: showMatchesStep,
		onProceed: showCoverageStep,
	});

	useMountEffect(() => {
		fetchActiveForm(ENTITY_REQUEST_ENTITY_TYPES.supplier)
			.then((activeForm) => {
				setQuestions(activeForm ? nullSafeGet('questions', activeForm) || [] : []);
			})
			.catch(() => {
				setFormLoadFailed(true);
			})
			.finally(() => {
				setQuestionsLoading(false);
			});

		// The tiers are snapshotted server-side on submit, so a new or draft request carries none — ask
		// the API who would review it instead. A failure leaves the generic tooltip in place: naming the
		// approvers is informational and must never block the form.
		if (!recordHasApprovalTiers) {
			fetchApprovalHierarchyPreview(ENTITY_REQUEST_ENTITY_TYPES.supplier)
				.then(setApprovalHierarchyPreview)
				.catch(() => {});
		}
	});

	const persist = (values) => {
		const payload = buildSupplierPayload(values);
		const answers = buildAnswers(questions, values);
		const entity = {
			entityType: ENTITY_REQUEST_ENTITY_TYPES.supplier,
			payload,
			answers,
		};
		const request = persistedId
			? updateRequest({ ...entity, id: persistedId })
			: createRequest(entity);
		return request.then((saved) => {
			const savedId = nullSafeGet('id', saved);
			if (savedId) {
				setPersistedId(savedId);
			}
			return saved;
		});
	};

	// Advancing is explicit per step rather than `step + 1`: the matches step sits between contact and
	// coverage but is only reached when the duplicate lookup actually finds something.
	const handleNext = () => {
		setErrorMessage(undefined);
		if (step === CONTACT_STEP) {
			form
				.validateFields(CONTACT_STEP_FIELDS)
				.then(checkForDuplicates)
				.catch(() => {});
			return;
		}
		if (step === MATCHES_STEP) {
			// Requesting the supplier they typed rather than one of the matches.
			clearSelectedSupplier();
			showCoverageStep();
			return;
		}
		setStep(QUESTIONS_STEP);
	};

	const handleBackToContactStep = () => {
		setErrorMessage(undefined);
		setStep(CONTACT_STEP);
	};

	// Picking one of the matches fills the request with that supplier's details, then carries on to coverage.
	const handleSelectSupplierMatch = (supplier) => {
		applyMatchToForm(supplier);
		showCoverageStep();
	};

	const handleSaveDraft = () => {
		setErrorMessage(undefined);
		setSavingDraft(true);
		const values = form.getFieldsValue(true);
		persist(values)
			.then(() => {
				setSavingDraft(false);
				onSuccess();
				hideModal();
			})
			.catch((error) => {
				setSavingDraft(false);
				setErrorMessage(
					typeof error === 'string' ? error : 'Something went wrong saving the draft.'
				);
			});
	};

	// Explicit final-step submit. We validate the whole form ourselves rather than using a native
	// submit button so advancing steps can never trigger an accidental submit.
	const handleSendRequest = () => {
		setErrorMessage(undefined);
		form
			.validateFields()
			.then((values) => {
				setSubmitting(true);
				return persist(values)
					.then((saved) => submitRequest(nullSafeGet('id', saved)))
					.then(() => {
						setSubmitting(false);
						onSuccess();
						hideModal();
					})
					.catch((error) => {
						setSubmitting(false);
						setErrorMessage(
							typeof error === 'string' ? error : 'Something went wrong submitting this request.'
						);
					});
			})
			.catch(() => {
				// Validation errors are surfaced inline on the questions step.
			});
	};

	// A request that has been through review carries its own tiers, sign-offs included; anything earlier
	// falls back to the API's preview of who would review it if it were submitted now. Either way the
	// link is the one the Suggested Entities table and the review modal carry: hover for the approvers
	// per tier, click for the full hierarchy. A company with no entity-request approval rule
	// (`hasApprovalHierarchy: false`) has no approvers to name, so the link is dropped entirely rather
	// than shown as a dead label — same for a preview that has yet to resolve, which keeps the subtitle
	// from flickering a link in and back out.
	const approvalHierarchyRecord = recordHasApprovalTiers ? record : approvalHierarchyPreview;

	const approversLink = hasApprovalTiers(approvalHierarchyRecord) ? (
		<ApprovalHierarchyDisplayEntityRequest
			record={approvalHierarchyRecord}
			label="Approvers"
			typographyClassName="footnoteregular"
		/>
	) : null;

	const modalTitle = (
		<div>
			<div className="flex flex-row items-start justify-between">
				<div className="h5medium text-character-primary">Request to add a Supplier</div>
			</div>
			{/* The approvers link runs on with the sentence rather than being pushed to the far edge, so
			    it reads as part of it. Inline-flex keeps its icon and label together when the line wraps. */}
			<div className="footnoteregular mt-1 text-character-secondary">
				Request a supplier your team works with that isn't on OpenWrench yet.
				{approversLink ? <> {approversLink}</> : null}
			</div>
		</div>
	);

	// The form fields only mount once questions have loaded; block persist/submit/advance actions
	// until then (and on a load failure) so nothing runs against unmounted/undefined fields. Also lock
	// out every footer action while any one is in flight: for a new request Save Draft and Send Request
	// both call persist() before persistedId is set, so leaving the others enabled lets two creates race
	// into a duplicate draft/submission. Each button still shows its own spinner via its `loading` prop.
	const actionInFlight = savingDraft || submitting || checkingDuplicates;
	const actionsDisabled = questionsLoading || formLoadFailed || actionInFlight;

	const footer = (
		<div className="flex flex-row items-center justify-between">
			{step === MATCHES_STEP ? (
				<OWButton type="default" onClick={handleBackToContactStep}>
					Back
				</OWButton>
			) : (
				<OWButton
					type="default"
					onClick={handleSaveDraft}
					loading={savingDraft}
					disabled={actionsDisabled}
				>
					Save Draft
				</OWButton>
			)}
			<div className="flex flex-row items-center" style={{ gap: '8px' }}>
				<OWButton type="default" onClick={hideModal}>
					Cancel
				</OWButton>
				{step === MATCHES_STEP ? (
					<OWButton type="primary" htmlType="button" onClick={handleNext}>
						No, Request My Supplier
					</OWButton>
				) : step < LAST_STEP ? (
					<OWButton
						type="primary"
						htmlType="button"
						onClick={handleNext}
						loading={checkingDuplicates}
						disabled={actionsDisabled}
					>
						Next
					</OWButton>
				) : (
					<OWButton
						type="primary"
						htmlType="button"
						onClick={handleSendRequest}
						loading={submitting}
						disabled={actionsDisabled}
					>
						Send Request
					</OWButton>
				)}
			</div>
		</div>
	);

	return (
		<Modal
			visible={visible}
			width={600}
			title={modalTitle}
			onCancel={hideModal}
			closable={true}
			destroyOnClose={true}
			footer={footer}
		>
			{questionsLoading ? (
				<div className="flex w-full justify-center py-8">
					<Spin />
				</div>
			) : formLoadFailed ? (
				<Alert
					type="error"
					showIcon
					message="Couldn't load the request form"
					description="Please close and try again — this request can't be submitted until the form loads."
				/>
			) : (
				<Form
					form={form}
					layout="vertical"
					initialValues={getSupplierRequestInitialValues(record, questions)}
				>
					{errorMessage ? <Alert message={errorMessage} type="error" className="mb-4" /> : null}
					{step === MATCHES_STEP ? (
						<div>
							<div className="h5medium mb-1 text-character-primary">
								{getSimilarSuppliersTitle(duplicateMatches.length)}
							</div>
							<SimilarSuppliersPanel
								suppliers={duplicateMatches}
								existingSupplierIds={existingSupplierIds}
								description="Pick one to fill this request with that supplier's details, or continue with the supplier you entered."
								onSelectSupplier={handleSelectSupplierMatch}
							/>
						</div>
					) : null}
					{/* All steps stay mounted (hidden) so values + "Select All" watches persist across steps. */}
					<div hidden={step !== CONTACT_STEP}>
						<SupplierContactFields />
					</div>
					<div hidden={step !== COVERAGE_STEP}>
						<SupplierCoverageFields
							form={form}
							problemTypes={problemTypes}
							fetchProblemTypes={fetchProblemTypes}
							fetchMultipleProblemTypes={fetchMultipleProblemTypes}
							locations={locations}
							fetchLocations={fetchLocations}
							fetchMultipleLocations={fetchMultipleLocations}
						/>
					</div>
					<div hidden={step !== QUESTIONS_STEP}>
						<EntityRequestAnswerFields questions={questions} />
					</div>
				</Form>
			)}
		</Modal>
	);
}

const mapStateToProps = (state) => ({
	problemTypes: state.problem_types,
	locations: state.locations,
	currentUser: state.session.currentUser,
});

const mapDispatchToProps = (dispatch, ownProps) => ({
	fetchActiveForm: (entityType) =>
		dispatch(fetchActiveEntityRequestForm(ownProps.userType)(entityType)),
	fetchApprovalHierarchyPreview: (entityType) =>
		dispatch(fetchEntityAddRequestApprovalHierarchyPreview(ownProps.userType)(entityType)),
	createRequest: (entity) =>
		dispatch(entityAddRequestsRestCrudThunks(ownProps.userType).create(entity)),
	updateRequest: (entity) =>
		dispatch(entityAddRequestsRestCrudThunks(ownProps.userType).update(entity)),
	submitRequest: (id) => dispatch(submitEntityAddRequest(ownProps.userType)(id)),
	searchSuppliers: (name, email) =>
		dispatch(fetchSuppliersForBuyers(ownProps.userType)(name, email)(DUPLICATE_SEARCH_COLLECTION)),
	// Membership check for exactly the matched facilities — no target collection, we only consume the
	// resolved rows. `supplierFacilityIds` is a supported filter on the distinct private-network query.
	fetchNetworkMembership: (supplierFacilityIds) =>
		dispatch(
			supplierPrivateNetworksGetDistinct(ownProps.userType)({}, null, null, null, {
				supplierFacilityIds: supplierFacilityIds.join(','),
			})
		),
	fetchProblemTypes: (params, targetCollectionName, pagination, sorting, filters, addTo) =>
		dispatch(
			problemTypesHeirarchical(ownProps.userType)(
				params,
				targetCollectionName,
				pagination,
				sorting,
				filters,
				addTo
			)
		),
	fetchMultipleProblemTypes: (ids, targetCollectionName) =>
		dispatch(problemTypesRestCrudThunks(ownProps.userType).readMultiple(ids, targetCollectionName)),
	fetchLocations: (params, targetCollectionName, pagination, sorting, filters, addTo) =>
		dispatch(
			fetchLocationsWithRegions(ownProps.userType)(
				params,
				targetCollectionName,
				pagination,
				sorting,
				filters,
				addTo
			)
		),
	fetchMultipleLocations: (ids, targetCollectionName) =>
		dispatch(locationsRestCrudThunks(ownProps.userType).readMultiple(ids, targetCollectionName)),
});

export default withUserType(connect(mapStateToProps, mapDispatchToProps)(RequestToAddSupplierForm));
